/**
 * Local Storage Cache for Google Places API
 * Reduces API calls by caching results locally
 */

const CACHE_PREFIX = 'places_cache_';
const CACHE_VERSION = '1.0';
const MAX_CACHE_AGE = 60 * 24 * 60 * 60 * 1000; // 60 days in milliseconds
const MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB max cache size

/**
 * Generate cache key for a location/query combination
 */
export const generateCacheKey = (location, query, radius) => {
  const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
  return `${CACHE_PREFIX}${lat.toFixed(4)}_${lng.toFixed(4)}_${query}_${radius}`;
};

/**
 * Generate cache key for place details
 */
export const generatePlaceDetailsKey = (placeId) => {
  return `${CACHE_PREFIX}details_${placeId}`;
};

/**
 * Check if cache entry is valid (not expired)
 */
const isCacheValid = (cacheEntry) => {
  if (!cacheEntry || !cacheEntry.timestamp) return false;
  const age = Date.now() - cacheEntry.timestamp;
  return age < MAX_CACHE_AGE;
};

/**
 * Get cached search results
 */
export const getCachedSearchResults = (location, query, radius) => {
  try {
    const key = generateCacheKey(location, query, radius);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const cacheEntry = JSON.parse(cached);
    if (!isCacheValid(cacheEntry)) {
      localStorage.removeItem(key);
      return null;
    }

    return cacheEntry.data;
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
};

/**
 * Cache search results
 */
export const cacheSearchResults = (location, query, radius, results) => {
  const key = generateCacheKey(location, query, radius);
  const cacheEntry = {
    version: CACHE_VERSION,
    timestamp: Date.now(),
    data: results
  };
  
  try {
    localStorage.setItem(key, JSON.stringify(cacheEntry));
    
    // Clean up old cache entries if cache is too large
    cleanupCache();
  } catch (error) {
    console.error('Error caching results:', error);
    // If quota exceeded, try to clean up and retry
    if (error.name === 'QuotaExceededError') {
      cleanupCache(true);
      try {
        localStorage.setItem(key, JSON.stringify(cacheEntry));
      } catch (retryError) {
        console.error('Cache still full after cleanup:', retryError);
      }
    }
  }
};

/**
 * Get cached place details
 */
export const getCachedPlaceDetails = (placeId) => {
  try {
    const key = generatePlaceDetailsKey(placeId);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const cacheEntry = JSON.parse(cached);
    if (!isCacheValid(cacheEntry)) {
      localStorage.removeItem(key);
      return null;
    }

    return cacheEntry.data;
  } catch (error) {
    console.error('Error reading place details cache:', error);
    return null;
  }
};

/**
 * Cache place details
 */
export const cachePlaceDetails = (placeId, details) => {
  try {
    const key = generatePlaceDetailsKey(placeId);
    
    // Convert LatLng objects to plain objects for storage
    const serializableDetails = { ...details };
    if (serializableDetails.geometry?.location) {
      const location = serializableDetails.geometry.location;
      // If it's a LatLng object (has methods), convert to plain object
      if (typeof location.lat === 'function') {
        serializableDetails.geometry.location = {
          lat: location.lat(),
          lng: location.lng()
        };
      }
    }
    
    const cacheEntry = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      data: serializableDetails
    };
    localStorage.setItem(key, JSON.stringify(cacheEntry));
  } catch (error) {
    console.error('Error caching place details:', error);
  }
};

/**
 * Clean up old cache entries
 */
const cleanupCache = (aggressive = false) => {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
    
    if (cacheKeys.length === 0) return;

    // Calculate total cache size
    let totalSize = 0;
    const cacheEntries = cacheKeys.map(key => {
      const value = localStorage.getItem(key);
      const size = new Blob([value]).size;
      totalSize += size;
      return { key, size, timestamp: JSON.parse(value)?.timestamp || 0 };
    });

    // If cache is too large or aggressive cleanup, remove oldest entries
    if (totalSize > MAX_CACHE_SIZE || aggressive) {
      // Sort by timestamp (oldest first)
      cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
      
      // Remove oldest 25% of entries
      const toRemove = Math.ceil(cacheEntries.length * 0.25);
      for (let i = 0; i < toRemove; i++) {
        localStorage.removeItem(cacheEntries[i].key);
      }
    }

    // Also remove expired entries
    const now = Date.now();
    cacheEntries.forEach(entry => {
      if (entry.timestamp && (now - entry.timestamp) > MAX_CACHE_AGE) {
        localStorage.removeItem(entry.key);
      }
    });
  } catch (error) {
    console.error('Error cleaning up cache:', error);
  }
};

/**
 * Clear all cache entries
 */
export const clearPlacesCache = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
    
    let totalSize = 0;
    let searchResultsCount = 0;
    let placeDetailsCount = 0;
    let expiredCount = 0;

    cacheKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        totalSize += new Blob([value]).size;
        const entry = JSON.parse(value);
        
        if (key.includes('details_')) {
          placeDetailsCount++;
        } else {
          searchResultsCount++;
        }

        if (!isCacheValid(entry)) {
          expiredCount++;
        }
      }
    });

    return {
      totalEntries: cacheKeys.length,
      searchResults: searchResultsCount,
      placeDetails: placeDetailsCount,
      expired: expiredCount,
      totalSize: totalSize,
      totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2)
    };
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return null;
  }
};

